/**
 * IAB TCF 2.0 Compliance Utilities
 * Ensures proper consent configuration for GDPR/CCPA compliance
 */

export interface IABTCFConsent {
  cmpId: number;
  cmpVersion: number;
  consentLanguage: string;
  gdprApplies: boolean;
  tcString: string;
  vendorConsents: Record<number, boolean>;
  purposeConsents: Record<number, boolean>;
  specialFeatureOptins: Record<number, boolean>;
}

/**
 * Generate IAB TCF 2.0 compliant consent string
 */
export function generateTCFConsent(preferences: {
  ad_Storage: 'granted' | 'denied';
  analytics_Storage: 'granted' | 'denied';
}): Partial<IABTCFConsent> {
  // Purpose 1: Store and/or access information on a device
  // Purpose 2: Select basic ads
  // Purpose 3: Create a personalised ads profile
  // Purpose 4: Select personalised ads
  // Purpose 5: Create a personalised content profile
  // Purpose 6: Select personalised content
  // Purpose 7: Measure ad performance
  // Purpose 8: Measure content performance
  // Purpose 9: Apply market research to generate audience insights
  // Purpose 10: Develop and improve products

  const purposeConsents: Record<number, boolean> = {
    1: true, // Storage - always true for necessary cookies
    2: preferences.ad_Storage === 'granted',
    3: preferences.ad_Storage === 'granted',
    4: preferences.ad_Storage === 'granted',
    5: preferences.ad_Storage === 'granted',
    6: preferences.ad_Storage === 'granted',
    7: preferences.ad_Storage === 'granted' || preferences.analytics_Storage === 'granted',
    8: preferences.analytics_Storage === 'granted',
    9: preferences.analytics_Storage === 'granted',
    10: preferences.analytics_Storage === 'granted',
  };

  // Vendor consents (Google Analytics, etc.)
  const vendorConsents: Record<number, boolean> = {
    // Google Analytics vendor ID: 755
    755: preferences.analytics_Storage === 'granted',
    // Add other vendors as needed
  };

  return {
    cmpId: 1, // Your CMP ID
    cmpVersion: 1,
    consentLanguage: 'en',
    gdprApplies: true,
    vendorConsents,
    purposeConsents,
    specialFeatureOptins: {},
  };
}

/**
 * Store IAB TCF consent string
 */
export function storeTCFConsent(consent: Partial<IABTCFConsent>): void {
  if (typeof window !== 'undefined') {
    // Store in __tcfapi for IAB TCF compliance
    (window as any).__tcfapi = (window as any).__tcfapi || function() {};
    
    // Store consent data
    (window as any).__iabConsent = consent;
    
    // Trigger consent update event
    if (typeof (window as any).__tcfapi === 'function') {
      (window as any).__tcfapi('addEventListener', 2, (tcData: any, success: boolean) => {
        if (success) {
          console.log('[IAB TCF] Consent updated:', tcData);
        }
      });
    }
  }
}

/**
 * Check if consent is properly configured
 */
export function isConsentProperlyConfigured(preferences: {
  ad_Storage: 'granted' | 'denied';
  analytics_Storage: 'granted' | 'denied';
} | null): boolean {
  if (!preferences) {
    return false;
  }

  // Ensure all required fields are present
  return (
    typeof preferences.ad_Storage === 'string' &&
    typeof preferences.analytics_Storage === 'string' &&
    (preferences.ad_Storage === 'granted' || preferences.ad_Storage === 'denied') &&
    (preferences.analytics_Storage === 'granted' || preferences.analytics_Storage === 'denied')
  );
}

declare global {
  interface Window {
    __tcfapi?: Function;
    __iabConsent?: Partial<IABTCFConsent>;
  }
}






